import { trackEvent } from '@/shared/utils/analyticsEvents';
import { captureException, type SeverityLevel } from '@sentry/react';
import { isRouteErrorResponse } from 'react-router';

/**
 * Extract an HTTP-like status code from a thrown value. Handles `ApiError`,
 * `Response` objects re-thrown by react-router loaders, and plain objects
 * shaped like `{ status: number }`.
 */
const extractStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
};

const NETWORK_FAILURE_PATTERNS = [
  'Failed to fetch',
  'NetworkError when attempting to fetch',
  'Load failed',
  'network connection was lost',
  // PixiJS asset loader surfaces transport failures (offline, CORS, 404 on
  // stale CDN path after a deploy, etc.) via this prefix. This is an
  // internal PixiJS message format so it could drift on major version
  // upgrades — revisit when bumping @pixi/* packages.
  '[Loader.load] Failed to load',
];

const matchesNetworkPattern = (message: string): boolean => NETWORK_FAILURE_PATTERNS.some((p) => message.includes(p));

/**
 * Detect browser "fetch failed before it left the box" errors. These are
 * always client-environment issues (offline, DNS, captive portal, ad-blocker,
 * corporate proxy, CSP, asset rollback, etc.) rather than bugs in our code.
 * Different browsers surface them with slightly different shapes:
 *   - Chrome/Edge: TypeError("Failed to fetch")
 *   - Firefox:     TypeError("NetworkError when attempting to fetch resource.")
 *   - Safari:      TypeError("Load failed") / "The network connection was lost."
 *
 * Privacy/anti-tracking extensions and corporate proxies sometimes surface
 * the same conditions with a parenthesized host suffix
 * (e.g. `Failed to fetch (api.example.com)`); those still match because
 * `includes` is substring-based.
 *
 * Also recognizes thrown `Response`s / react-router `ErrorResponse`s whose
 * body string carries one of these patterns — that's the shape we end up
 * with when a loader catches a transport failure and re-throws it via
 * `throw new Response(error.message, { status })`.
 */
export const isNetworkFailure = (error: unknown): boolean => {
  if (error instanceof Error) {
    if (error.name === 'NetworkError' || error.name === 'AbortError') return true;
    return matchesNetworkPattern(error.message ?? '');
  }

  if (isRouteErrorResponse(error) && typeof error.data === 'string') {
    return matchesNetworkPattern(error.data);
  }

  return false;
};

/**
 * `fetch()` rejects with `DOMException(name='AbortError')` when its `signal`
 * fires; Node/jsdom/test envs may surface a plain `Error` with the same name.
 * Match on `name` rather than `instanceof DOMException` so both work.
 */
export const isAbortError = (error: unknown): boolean => {
  return error instanceof Error && error.name === 'AbortError';
};

// IndexedDB / Dexie failures that originate in the browser or OS rather than in
// our code. The app degrades gracefully when these happen (offline stores just
// return empty / skip persistence), so they're noise in the Sentry error feed.
//   - UnknownError ("Internal error."): the well-known Chrome IndexedDB bug
//     (disk I/O, profile corruption); Dexie even ships a workaround for it and
//     logs "Dexie: Workaround for Chrome UnknownError on open()".
//   - DatabaseClosedError: Dexie's wrapper when the connection drops because the
//     underlying open() failed (this is what surfaces from loadChats above).
//   - QuotaExceededError: disk full / storage pressure / eviction.
//   - InvalidStateError: IndexedDB disabled or blocked (private mode, blocked
//     site storage, etc.).
const INDEXEDDB_UNAVAILABLE_ERROR_NAMES = [
  'UnknownError',
  'DatabaseClosedError',
  'QuotaExceededError',
  'InvalidStateError',
];

/**
 * Detect IndexedDB/Dexie "the browser can't give us local storage" failures.
 * These are client-environment conditions, not app bugs. Dexie often wraps the
 * underlying `DOMException` and embeds its name in the message (e.g. a
 * `DatabaseClosedError` whose message is "UnknownError Internal error."), so we
 * check both the error `name` and the message text.
 *
 * Inspects any error-*like* object rather than requiring `instanceof Error`: a
 * raw `DOMException` (which is what IndexedDB rejects with, e.g. an
 * `UnknownError`) is NOT an `Error` instance in browsers, but it does expose
 * string `name`/`message` properties.
 */
export const isStorageUnavailable = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const { name, message } = error as { name?: unknown; message?: unknown };
  if (typeof name === 'string') {
    // A recognized error name is an exact, reliable signal. If the name is set
    // but unrecognized, trust it and don't scan the message — that avoids false
    // positives from app errors that merely mention one of these strings
    // (e.g. "Failed to process UnknownError response").
    return INDEXEDDB_UNAVAILABLE_ERROR_NAMES.includes(name);
  }
  // Only nameless error-like objects fall back to message scanning, which still
  // catches a Dexie error that embeds the inner DOMException name in its message.
  return typeof message === 'string' && INDEXEDDB_UNAVAILABLE_ERROR_NAMES.some((n) => message.includes(n));
};

type SendAnalyticsErrorOptions = {
  /** Additional key/value pairs to include in the Sentry `extra` payload. */
  extra?: Record<string, unknown>;
  /** Override the inferred Sentry severity. */
  level?: SeverityLevel;
  /**
   * Force the error to be classified as a network failure for Sentry severity
   * purposes. Use this for call sites where the underlying cause is known to
   * be transport-level but the thrown value itself carries no distinguishing
   * message (e.g. module `worker.onerror` which, per spec, fires a generic
   * Event with empty message/filename on load failure).
   */
  treatAsNetworkFailure?: boolean;
  /**
   * Opt out of the default 401 → warning downgrade. The common case for a 401
   * is an idle-tab session expiry (expected, noisy), so we downgrade those to
   * `warning`. But some callers have independent evidence that the 401 is
   * anomalous — e.g. a client-authenticated user receiving 401 from our API,
   * which points at a backend auth misconfiguration, clock skew, or revoked
   * key rather than a user-session issue. Those callers set this flag so the
   * event is recorded at `error` level and isn't hidden in the noise.
   */
  unexpectedAuthFailure?: boolean;
};

/**
 * If `error` is a thrown `Response` (already consumed by react-router into a
 * route `ErrorResponse`), turn it into a synthesized `Error` whose message
 * carries the actual response body (when it's a string). This way Sentry
 * groups events by the underlying cause rather than by the generic
 * `[origin] from` boilerplate every caller of `sendAnalyticsError` shares.
 *
 * Returns `null` when `error` isn't a Response/ErrorResponse and the caller
 * should fall back to its default synthesized message.
 */
const errorFromResponse = (error: unknown): Error | null => {
  if (isRouteErrorResponse(error)) {
    const body = typeof error.data === 'string' ? error.data : error.statusText || `HTTP ${error.status}`;
    const synthesized = new Error(body);
    synthesized.name = `RouteErrorResponse(${error.status})`;
    return synthesized;
  }
  if (typeof Response !== 'undefined' && error instanceof Response) {
    const synthesized = new Error(error.statusText || `HTTP ${error.status}`);
    synthesized.name = `Response(${error.status})`;
    return synthesized;
  }
  return null;
};

export const sendAnalyticsError = (
  origin: string,
  from: string,
  error: Error | unknown,
  description?: string,
  options?: SendAnalyticsErrorOptions
) => {
  // Use console.warn to avoid captureConsoleIntegration creating a duplicate Sentry event
  // (it captures 'error' level); the error is already sent to Sentry via captureException below.
  console.warn(`[${origin}] ${from}:`, error);

  try {
    const errorString = JSON.stringify(
      error instanceof Error
        ? {
            name: error.name,
            message: error.message,
            stack: error.stack,
          }
        : error
    );
    trackEvent(`[${origin}].error`, {
      title: from,
      description,
      error: errorString,
    });
  } catch (error) {
    console.error('Error sending Mixpanel error', error);
  }

  try {
    let sentryError: Error;
    if (error instanceof Error) {
      sentryError = error;
    } else {
      sentryError =
        errorFromResponse(error) ?? new Error(`[${origin}] ${from}${description ? `: ${description}` : ''}`);
    }

    // Downgrade expected client-environment conditions that aren't app bugs:
    //   - 401 auth expirations (idle tab, stale session)
    //   - network / fetch failures (offline, DNS, proxy, asset rollback)
    //   - caller explicitly flagged a transport-level failure (e.g. module
    //     `worker.onerror` events that carry no message)
    // `navigator.onLine === false` alone is NOT sufficient — genuine app bugs
    // can still happen while the browser is offline, and we don't want to
    // silently downgrade those. We only use `offline` as a corroborating
    // signal alongside one of the above.
    const status = extractStatus(error);
    const networkFailure = isNetworkFailure(error) || options?.treatAsNetworkFailure === true;
    const expiredSession = status === 401 && options?.unexpectedAuthFailure !== true;
    const storageUnavailable = isStorageUnavailable(error);
    const isExpectedClientCondition = expiredSession || networkFailure || storageUnavailable;
    const level: SeverityLevel = options?.level ?? (isExpectedClientCondition ? 'warning' : 'error');

    captureException(sentryError, {
      level,
      extra: {
        origin,
        from,
        description,
        ...(status !== undefined ? { status } : {}),
        ...(networkFailure ? { networkFailure: true } : {}),
        ...(storageUnavailable ? { storageUnavailable: true } : {}),
        ...(typeof navigator !== 'undefined' ? { online: navigator.onLine } : {}),
        ...(error instanceof Error ? {} : { originalError: error }),
        ...(options?.extra ?? {}),
      },
      tags: {
        origin,
        from,
        description,
      },
    });
  } catch (error) {
    console.error('Error sending Sentry error', error);
  }
};
